import { booleanAttribute, Directive, effect, ElementRef, forwardRef, inject, InjectionToken, input, Renderer2 } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { fromEvent, Observable, Subject, switchMap } from 'rxjs';


export interface Scrollable {
    scrollTo(id: string): void;
    scrollingEvents: Observable<unknown>;
    getBoundingClientRect(): DOMRect;
}

export const SCROLLABLE_ELEMENT = new InjectionToken('SCROLLABLE_ELEMENT');


@Directive({
    selector: '[scrollable]',
    providers: [
        { provide: SCROLLABLE_ELEMENT, useExisting: forwardRef(() => ScrollableDirective) },
    ]
})

export class ScrollableDirective implements Scrollable {

    scrollable = input(true, { transform: booleanAttribute });

    private elementRef = inject(ElementRef);

    private renderer = inject(Renderer2);

    private parent = inject<Scrollable>(SCROLLABLE_ELEMENT, { skipSelf: true, optional: true });

    readonly scrollingEvents = new Subject();

    constructor() {
        effect(() => {
            this.renderer[this.scrollable() ? 'addClass' : 'removeClass'](this.elementRef.nativeElement, 'overflow-scroll');
            this.scrollingEvents.next('scroll');
        });

        const scrollingEvents = toObservable(this.scrollable).pipe(
            switchMap(scrollable => {
                if (scrollable) {
                    return fromEvent(this.elementRef.nativeElement, 'scroll');
                } else {
                    return this.parent ? this.parent.scrollingEvents : fromEvent(document.body, 'scroll');
                }
            })
        );

        scrollingEvents.pipe(takeUntilDestroyed()).subscribe(this.scrollingEvents);
    }

    scrollTo(id: string) {
        if (!this.scrollable()) {
            return;
        }
        const el = document.getElementById(id);
        if (el) {
            this.elementRef.nativeElement.scrollTo({ top: el.offsetTop - this.elementRef.nativeElement.offsetTop , behavior: 'smooth' });
        }
    }

    getBoundingClientRect() {
        if (this.scrollable()) {
            return this.elementRef.nativeElement.getBoundingClientRect();
        } else {
            return this.parent?.getBoundingClientRect() || document.body.getBoundingClientRect();
        }
    }
}
